// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Tag } from '@carbon/react';
import { getNumberAttr } from '../../../utils/planAttributes';
import { getExecutionWindow } from '../../../utils/planLifecycle';
import { useSiteTime } from '../../../hooks/useSiteTime';
import type { HitlApproval } from '../../../types';

/** Why no plan is running on the Live tab right now. */
export type UnplannedReason =
  | 'rejected'
  | 'unapproved'
  | 'not-started'
  | 'finished'
  | 'no-recommendation';

/**
 * The reason for the day's plan not running, read off the approval the Live tab
 * fell back to. `unapproved` covers a plan still pending as well as one nobody
 * reviewed before its window opened: either way nobody approved it, and
 * approving it late doesn't make the plan run.
 */
export function unplannedReason(approval: HitlApproval | undefined, now: Date): UnplannedReason {
  if (!approval) return 'no-recommendation';
  if (approval.state === 'REJECTED') return 'rejected';
  if (approval.state !== 'APPROVED') return 'unapproved';
  const window = getExecutionWindow(approval.attributes);
  return window && now.getTime() < window.start.getTime() ? 'not-started' : 'finished';
}

const HEADLINES: Record<UnplannedReason, string> = {
  rejected: 'No plan was approved to run today',
  unapproved: 'No plan was approved to run today',
  'not-started': 'Today’s plan hasn’t started yet',
  finished: 'Today’s plan has finished',
  'no-recommendation': 'No recommendation for today',
};

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="hs-card-stat">
      <span className="hs-card-stat-label">{label}</span>
      <span className="hs-card-stat-value live-hero-stat-value--muted">{value}</span>
      {note && <span className="hs-card-stat-note">{note}</span>}
    </div>
  );
}

export interface LiveUnplannedHeroProps {
  reason: UnplannedReason;
  /** Today's approval when there is one; absent for `no-recommendation`. */
  approval?: HitlApproval;
  /** Whether a baseline curve could be drawn; false when the narration was unavailable. */
  hasBaseline: boolean;
  now: Date;
}

/**
 * The Live tab's banner on a day no plan is running. It sits where the green
 * "executing now" hero does and in the same shape, but amber — or grey when
 * there was no recommendation at all — so an operator can't mistake measured
 * data under normal controls for a plan being followed.
 */
export default function LiveUnplannedHero({
  reason,
  approval,
  hasBaseline,
  now,
}: LiveUnplannedHeroProps) {
  const { format } = useSiteTime();
  const day = format(now, 'dateShort', '').toUpperCase();
  const window = getExecutionWindow(approval?.attributes);
  const decidedOn = format(approval?.updatedAt ?? approval?.createdAt, 'dateTime', '');
  const unapproved = reason === 'rejected' || reason === 'unapproved';
  const idle = reason === 'no-recommendation';

  const cause: Record<UnplannedReason, string> = {
    rejected: decidedOn
      ? `Today’s recommendation was rejected on ${decidedOn}.`
      : 'Today’s recommendation was rejected.',
    unapproved: 'Today’s recommendation wasn’t approved.',
    'not-started': window
      ? `Today’s approved plan starts at ${format(window.start, 'clock')}.`
      : 'Today’s approved plan hasn’t started.',
    finished: window
      ? `Today’s approved plan ended at ${format(window.end, 'clock')}.`
      : 'Today’s approved plan has ended.',
    'no-recommendation': 'Helios didn’t raise a recommendation for today.',
  };
  const comparison = hasBaseline
    ? 'The site is running its normal controls, so the charts compare live readings against the optimizer’s baseline. No recommended curve is shown.'
    : 'The site is running its normal controls. There’s no baseline for today, so the charts show live readings only.';

  // `missedSavingsUsd` once the day settles; until then the plan's own estimate.
  const notCaptured =
    getNumberAttr(approval?.attributes, 'missedSavingsUsd') ??
    getNumberAttr(approval?.attributes, 'totalCostSavings');

  return (
    <div className={`live-hero ${idle ? 'live-hero--idle' : 'live-hero--warning'}`}>
      <div className="hs-card-row">
        <span className="hs-card-badge live-hero-badge">
          {idle ? 'NO RECOMMENDATION' : 'NOT OPTIMIZED'}
          {day && ` · ${day}`}
          {' · BASELINE OPERATION'}
        </span>
        <Tag type={idle ? 'gray' : 'warm-gray'} size="sm" className="live-hero-tag">
          {unapproved ? 'No approved plan' : idle ? 'No plan' : 'Outside plan window'}
        </Tag>
      </div>

      <div className="hs-card-plan-title">{HEADLINES[reason]}</div>
      <p className="hs-card-plan-desc">
        {cause[reason]} {comparison}
      </p>

      {unapproved && (
        <div className="hs-card-stats live-hero-stats">
          {notCaptured !== undefined && (
            <Stat
              label="Savings not captured"
              value={`$${Math.abs(notCaptured).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}`}
              note="Est. value of the unapproved plan."
            />
          )}
          <Stat
            label="Energy saved vs. baseline"
            value="0%"
            note="The baseline is the plan today."
          />
        </div>
      )}
    </div>
  );
}
